import axios from "axios";
import logger from "../Utils/logger";

export const Hipster_Base_URL = process.env.REACT_APP_BASE_URL;
export const Hipster_Base_Image_URL = process.env.REACT_APP_BASE_URL + '/uploads/';

/**
 * ARCHITECTURE NOTE:
 * All caching, retry logic, and data synchronization is handled by React Query (useApiQueries.js).
 * These functions are simple API calls - do NOT add caching logic here.
 * React Query manages: cache invalidation, stale time, retry behavior, cross-tab sync, etc.
 *
 * Network/timeout errors are rethrown so React Query can pause/retry via onlineManager.
 * See: src/APICalling/useApiQueries.js for all React Query hooks
 */

/** Avoid indefinite hangs when the connection drops mid-request */
const REQUEST_TIMEOUT_MS = 30000;

const axiosConfig = {
  timeout: REQUEST_TIMEOUT_MS,
  headers: { Accept: "application/json" },
};

const logAndRethrow = (method, endpoint, error) => {
  const detail = {
    message: error?.message,
    code: error?.code,
    status: error?.response?.status,
  };
  logger.error(`${method} ${endpoint} failed`, detail);
  // Always rethrow — returning null/error objects makes React Query treat failures as success
  throw error;
};

// ======================================================================================================================================
//  BASE API FUNCTIONS
// ======================================================================================================================================

/**
 * GET request wrapper
 * @param {string} endpoint - API endpoint path (e.g., '/outlets')
 * @param {object} params - Query parameters
 * @returns {Promise} Response data
 */
const getRequest = async (endpoint, params = null) => {
  try {
    const panel = "customer";
    const url = `${Hipster_Base_URL}/api/v1${endpoint}?panel=${panel}`;
    const response = await axios.get(url, { ...axiosConfig, params });
    return response.data;
  } catch (error) {
    logAndRethrow('GET', endpoint, error);
  }
};

/**
 * POST request wrapper
 * @param {string} endpoint - API endpoint path
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
const postRequest = async (endpoint, data) => {
  try {
    const url = `${Hipster_Base_URL}/api/v1${endpoint}`;
    const response = await axios.post(url, data, axiosConfig);
    return response.data;
  } catch (error) {
    logAndRethrow('POST', endpoint, error);
  }
};

/**
 * DELETE request wrapper
 * @param {string} endpoint - API endpoint path
 * @returns {Promise} Response data
 */
const deleteRequest = async (endpoint) => {
  try {
    const url = `${Hipster_Base_URL}/api/v1${endpoint}`;
    const response = await axios.delete(url, axiosConfig);
    return response.data;
  } catch (error) {
    logAndRethrow('DELETE', endpoint, error);
  }
};

// ======================================================================================================================================
//  GET ENDPOINTS
// ======================================================================================================================================

export const getAppointmentType = (data) => getRequest('/booking-types', data);
export const getWebSettings = (data) => getRequest('/web-settings', data);
export const getCountries = () => getRequest('/countries');
export const getOutletType = (data) => getRequest('/outlet-types', data);
export const getStaff = (data) => getRequest('/therapists', data);
export const getOutletTiming = (data) => getRequest('/outlet-timings/new', data);
export const getBookingDates = (data) => postRequest('/outlet-timings/get/day-slot-availability', data);
export const getBookingSlots = (data) => postRequest('/outlet-timings/get/slots', data);
export const checkBookingSlots = (data) => postRequest('/outlet-timings/check/slots', data);
export const checkSlotAvailable = (data) => postRequest('/outlet-timings/check/slot-available', data);
export const getTimeSlots = (data) => getRequest('/bookings/timeslots/available/therapist', data);
export const getTimeSlotsBasedOnServices = (data) => getRequest('/bookings/timeslots/available/service', data);
export const getServiceAddonsList = (data) => getRequest('/addon-category/addon/list', data);

export const getSelectedOutlet = async (id, data) => {
  if (id === "null") return null;
  return getRequest(`/outlets/${id}`, data);
};

export const bookingDetail = (id, data) => getRequest(`/bookings/${id}`, data);

// ======================================================================================================================================
//  CACHED GET ENDPOINTS (wrapped by React Query)
// ======================================================================================================================================
// These are called ONLY by React Query hooks. All caching is handled by React Query.
// See useApiQueries.js for cache configuration (staleTime, gcTime, retry behavior, etc.)

export const getServicesCategoryWithTimeCache = (data) => getRequest('/service-category', data);
export const getOutletsWithTimeCache = (data) => getRequest('/outlets', data);
export const getOutletTypeWithTimeCache = (data) => getRequest('/outlet-types', data);
export const getAppointmentTypeWithTimeCache = (data) => getRequest('/booking-types', data);
export const getServiceAddonsWithTimeCache = (data) => getRequest('/addon-category/addon/list', data);
export const getOutletTimingWithTimeCache = (data) => getRequest('/outlet-timings/new', data);
export const getStaffWithTimeCache = (data) => getRequest('/therapists', data);
export const getBookingDatesWithTimeCache = (data) => postRequest('/outlet-timings/get/day-slot-availability', data);
export const getTimeSlotsWithTimeCache = (data) => getRequest('/bookings/timeslots/available/therapist', data);
export const getTimeSlotsBasedOnServicesWithTimeCache = (data) => getRequest('/bookings/timeslots/available/service', data);
export const getServiceCategoriesLight = (data) => getRequest('/service-category', { ...data, fields: 'id,name,image' });
export const getServicesByCategoryId = (categoryId, data) => getRequest(`/service-category/${categoryId}/services`, data);
export const getVouchersList = (data) => getRequest('/vouchers/online-listing', data);
export const getVouchersListWithTimeCache = (data) => getRequest('/vouchers/online-listing', data);



// ======================================================================================================================================
//  POST ENDPOINTS (wrapped by React Query mutations)
// ======================================================================================================================================

export const postBlockBookingData = (data) => postRequest('/bookings/block', data);
export const postBookingData = (data) => postRequest('/bookings/create', data);
export const cancelBooking = (data) => postRequest('/bookings/item/cancel', data);
export const rescheduleBooking = (data) => postRequest('/bookings/item/reschedule', data);
export const postFormData = (data) => postRequest('/users/create', data);
export const checkUser = (data) => postRequest('/users/booking/create', data);
export const getCustomerDetails = (data) => postRequest('/users/customer/details', data);
export const postSMSData = (data) => postRequest('/otp/send-sms', data);
export const postWhatsAppData = (data) => postRequest('/otp/send-whatsapp', data);
export const postOTP = (data) => postRequest('/otp/verify', data);
export const postRecaptchaVerify = (data) => postRequest('/recaptcha/verify', data);
export const checkOtpAttempts = (data) => postRequest('/otp/check-attempts', data);
export const recordOtpAttempt = (data) => postRequest('/otp/record-attempt', data);
export const postVoucherOrder = (data) => postRequest('/vouchers/online-order', data);
export const getVoucherReceipt = (orderId) => getRequest(`/vouchers/online-order/${orderId}/receipt`);


// ======================================================================================================================================
//  DELETE ENDPOINTS (wrapped by React Query mutations)
// ======================================================================================================================================

// export const deleteBooking = (bookingID) => deleteRequest(`/bookings/destroy/${bookingID}/?panel=customer`);
export const deleteBooking = (uuid) => deleteRequest(`/bookings/delete/web/${uuid}`);
